import React, { useState, useEffect, useContext } from 'react';
import { CustomerContext } from '../App';
import './LoanOverview.css';

function LoanOverview({ loans }) {
  const { selectedCustomerId } = useContext(CustomerContext);
  const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5001';

  const [mergedData, setMergedData] = useState([]);
  const [filterText, setFilterText] = useState('');
  const [sortKey, setSortKey] = useState('propertyName');
  const [sortDirection, setSortDirection] = useState('asc');

  // Upload-Modal States
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [uploadDocName, setUploadDocName] = useState('');
  const [uploadFile, setUploadFile] = useState(null);
  const [uploadUserMessage, setUploadUserMessage] = useState('');
  const [uploadMessage, setUploadMessage] = useState('');

  useEffect(() => {
    if (!loans) return;
    // Duplikate vermeiden (falls es doppelte creditId gibt)
    const uniqueLoans = Array.from(
      new Map(loans.map(loan => [loan.creditId, loan])).values()
    );
    setMergedData(uniqueLoans);
  }, [loans]);

  const sortLoans = (data, key, direction) => {
    const sorted = [...data].sort((a, b) => {
      const aVal = a[key];
      const bVal = b[key];
      if (typeof aVal === 'string') {
        return direction === 'asc'
          ? aVal.localeCompare(bVal)
          : bVal.localeCompare(aVal);
      } else {
        return direction === 'asc'
          ? aVal - bVal
          : bVal - aVal;
      }
    });
    return sorted;
  };

  useEffect(() => {
    setMergedData(prev => sortLoans(prev, sortKey, sortDirection));
  }, [sortKey, sortDirection]);

  const filtered = mergedData.filter(loan => {
    const search = filterText.toLowerCase();
    return (
      (loan.propertyName && loan.propertyName.toLowerCase().includes(search)) ||
      (loan.creditId && loan.creditId.toString().includes(search))
    );
  });

  const getProgressPercentage = (loan) => {
    if (!loan.duration || !loan.yearsRemaining) return 0;
    const yearsPassed = loan.duration - loan.yearsRemaining;
    return (yearsPassed / loan.duration) * 100;
  };

  // Upload-Funktion ähnlich wie im Documents-Component
  async function handleUpload(e) {
    e.preventDefault();
    setUploadMessage('');

    if (!uploadDocName || !uploadFile) {
      setUploadMessage('Bitte Dokumentenname + Datei auswählen.');
      return;
    }

    try {
      const formData = new FormData();
      formData.append('docName', uploadDocName);
      formData.append('file', uploadFile);
      formData.append('userMessage', uploadUserMessage);
      formData.append('customerId', selectedCustomerId || '');

      const response = await fetch(`${API_URL}/api/uploadDocument`, {
        method: 'POST',
        body: formData
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(errorText);
      }

      const result = await response.json();
      setUploadMessage(result.message || 'Dokument erfolgreich hochgeladen.');
      setUploadDocName('');
      setUploadFile(null);
      setUploadUserMessage('');

      // Modal nach erfolgreichem Upload schließen
      setShowUploadModal(false);
    } catch (error) {
      setUploadMessage('Fehler beim Hochladen: ' + error.message);
    }
  }

  return (
    <div className="loan-overview">
      <input
        type="text"
        placeholder="Filter nach Wohnungsname"
        value={filterText}
        onChange={(e) => setFilterText(e.target.value)}
        className="filter-input"
      />

      <div className="sort-controls">
        <label>Sortieren nach: </label>
        <select value={sortKey} onChange={e => setSortKey(e.target.value)}>
          <option value="propertyName">Wohnungsname</option>
          <option value="payment">Monatliche Zahlung</option>
          <option value="interestRate">Zinssatz</option>
          <option value="remainingCredit">Verbleibender Kredit</option>
          <option value="duration">Dauer</option>
        </select>

        <label>Richtung: </label>
        <select value={sortDirection} onChange={e => setSortDirection(e.target.value)}>
          <option value="asc">Aufsteigend</option>
          <option value="desc">Absteigend</option>
        </select>
      </div>

      {filtered.length === 0 ? (
        <div className="no-data-message">
          <h3>Keine Kreditdaten gefunden</h3>
          <p>
            Es sieht so aus, als wären deine Kreditdaten noch unvollständig oder fehlen. 
            Du hast zwei Möglichkeiten:
          </p>
          <ul>
            <li>
              <strong>Per E-Mail:</strong>{' '}
              <a href="mailto:info@service.prosperly.de">info@service.prosperly.de</a>
            </li>
            <li>
              <strong>Upload-Tool:</strong>{' '}
              <span
                className="upload-link"
                onClick={() => setShowUploadModal(true)}
                style={{ cursor: 'pointer', color: 'blue', textDecoration: 'underline' }}
              >
                hier klicken
              </span>{' '}
              – wir pflegen deine Daten innerhalb von 24 Stunden ein.
            </li>
          </ul>
        </div>
      ) : (
        filtered.map((loan, idx) => (
          <div className="loan-card" key={loan.creditId || idx}>
            <div className="loan-card-header">
              <h3>{loan.propertyName || `Kredit ${loan.creditId}`}</h3>
              <div className="loan-remaining-time">
                {loan.yearsRemaining} restliche Jahre
              </div>
            </div>

            <div className="progress-bar-container">
              <div
                className="progress"
                style={{ width: `${getProgressPercentage(loan)}%` }}
              />
            </div>

            <div className="loan-card-row">
              <div className="loan-card-item">
                <label>Monatliche Zahlung</label>
                <span>€{loan.payment.toLocaleString()}</span>
              </div>
              <div className="loan-card-item">
                <label>Zinssatz</label>
                <span>{loan.interestRate.toFixed(2)}%</span>
              </div>
              <div className="loan-card-item">
                <label>Verbleibender Kredit</label>
                <span>€{loan.remainingCredit.toLocaleString()}</span>
              </div>
              <div className="loan-card-item">
                <label>Dauer</label>
                <span>{loan.duration} Jahre</span>
              </div>
            </div>
          </div>
        ))
      )}

      {/* Upload Modal */}
      {showUploadModal && (
        <div className="upload-modal-overlay" onClick={() => setShowUploadModal(false)}>
          <div className="upload-modal" onClick={e => e.stopPropagation()}>
            <button className="close-upload-modal" onClick={() => setShowUploadModal(false)}>
              ×
            </button>
            <h2>Neues Dokument hochladen</h2>
            <form onSubmit={handleUpload} className="upload-form">
              {uploadMessage && <p className="upload-message">{uploadMessage}</p>}
              <div className="upload-form-group">
                <label>Dokumentenname</label>
                <input
                  type="text"
                  value={uploadDocName}
                  onChange={(e) => setUploadDocName(e.target.value)}
                  placeholder="z.B. Mietvertrag"
                />
              </div>
              <div className="upload-form-group">
                <label>Zusätzliche Bemerkungen (optional)</label>
                <textarea
                  value={uploadUserMessage}
                  onChange={(e) => setUploadUserMessage(e.target.value)}
                  placeholder="z.B. Hier sind wichtige Infos..."
                />
              </div>
              <div className="upload-form-group">
                <label>Datei hochladen</label>
                <input
                  type="file"
                  accept=".pdf,image/*"
                  onChange={(e) => setUploadFile(e.target.files[0])}
                />
              </div>
              <button type="submit" className="btn-submit-upload">
                Hochladen
              </button>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

export default LoanOverview;
